// CRC-32 (the zip polynomial) of an upload, sent to the backend when the
// upload is finalized. The backend stores it on the storage object so a
// bulk download can be served as one zip assembled server side, which
// needs every file's CRC-32 up front.

const CRC32_READ_CHUNK_BYTES = 8 * 1024 * 1024;

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

// Continues `crc` (the finished CRC-32 of the bytes before `bytes`, 0 to
// start) over `bytes`, so a large file can be processed chunk by chunk.
export const updateCrc32 = (crc: number, bytes: Uint8Array): number => {
  let c = (crc ^ 0xffffffff) >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC32_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
};

export const formatCrc32 = (crc: number) =>
  (crc >>> 0).toString(16).padStart(8, '0');

// Reads the file sequentially in its own pass, independent of the upload's
// chunks: a resumable upload can re-send or skip ranges after a retry, so
// its chunks can't be trusted to cover every byte exactly once, in order.
export const computeFileCrc32 = async (
  file: Blob,
  signal?: AbortSignal,
): Promise<string> => {
  let crc = 0;
  for (
    let offset = 0;
    offset < file.size;
    offset += CRC32_READ_CHUNK_BYTES
  ) {
    if (signal?.aborted) {
      throw new DOMException('CRC-32 aborted', 'AbortError');
    }
    const chunk = file.slice(offset, offset + CRC32_READ_CHUNK_BYTES);
    crc = updateCrc32(crc, new Uint8Array(await chunk.arrayBuffer()));
  }
  return formatCrc32(crc);
};
